/**
 * Common date helpers. All return Date objects at midnight (00:00:00)
 * except `now()` (current instant) and `toDateString()` (returns a string).
 */

/** Current instant (same as `new Date()`). */
export function now(): Date {
  return new Date()
}

/** Today at 00:00:00 local time. */
export function today(): Date {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return d
}

/** Tomorrow at 00:00:00 local time. */
export function tomorrow(): Date {
  const d = today()
  d.setDate(d.getDate() + 1)
  return d
}

/** Yesterday at 00:00:00 local time. */
export function yesterday(): Date {
  const d = today()
  d.setDate(d.getDate() - 1)
  return d
}

/** First day of the current month at 00:00:00 local time. */
export function firstDayOfMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth(), 1)
}

/** Last day of the current month at 00:00:00 local time. */
export function lastDayOfMonth(date: Date = new Date()): Date {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0)
}

/** Alias for firstDayOfMonth. */
export function startOfMonth(date: Date = new Date()): Date {
  return firstDayOfMonth(date)
}

/** Last day of the current month at 23:59:59.999 local time. */
export function endOfMonth(date: Date = new Date()): Date {
  const d = lastDayOfMonth(date)
  d.setHours(23, 59, 59, 999)
  return d
}

/** Formats a Date as `yyyy-mm-dd` (zero-padded, local time). Returns empty string for invalid dates. */
export function toDateString(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  const str = `${y}-${m}-${d}`
  return str.includes('NaN') ? '' : str
}
